import KeenSlider from 'keen-slider';
import { query } from '../core/dom.js';

export function initBlogSlider(ctx) {
  const track = query('[data-home-blog-slider-track]');
  if (!track) {
    return ctx;
  }

  const container = track.closest('[data-home-blog-slider]');
  const prevBtn = container?.querySelector('[data-home-blog-prev]');
  const nextBtn = container?.querySelector('[data-home-blog-next]');
  const sliderContainer = container;
  const basePadding = parseFloat(getComputedStyle(track).paddingInlineEnd) || 0;

  track.classList.remove('is-ready');
  track.setAttribute('aria-hidden', 'true');
  const getCurrentSpacing = () => {
    const details = ctx.blogSliderInstance?.track?.details;
    // fallback pra 0 se ainda não existe
    return Number.isFinite(details?.spacing) ? details.spacing : 0;
  };

  const adjustTrackPadding = () => {
    if (!sliderContainer) {
      return;
    }

    const lastSlideEl = track.querySelector('.keen-slider__slide:last-child');
    if (!lastSlideEl) {
      track.style.paddingInlineEnd = `${basePadding}px`;
      return;
    }

    const sliderWidth = sliderContainer.getBoundingClientRect().width;
    const slideRect = lastSlideEl.getBoundingClientRect();
    const slideStyles = getComputedStyle(lastSlideEl);
    const marginInlineEnd = parseFloat(slideStyles.marginInlineEnd);
    const marginRight = parseFloat(slideStyles.marginRight);
    const marginEnd = Number.isFinite(marginInlineEnd)
      ? marginInlineEnd
      : Number.isFinite(marginRight)
        ? marginRight
        : 0;
    const spacing = getCurrentSpacing();
    const slideFullWidth = slideRect.width + marginEnd + spacing;
    const extraPadding = Math.max(0, sliderWidth - slideFullWidth);
    track.style.paddingInlineEnd = `${basePadding + extraPadding}px`;
  };

  const setNavButtonState = (button, disabled) => {
    if (!button) {
      return;
    }

    button.disabled = disabled;
    button.classList.toggle('is-disabled', disabled);
    button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
  };

  const toggleBlogTrackWidth = (details) => {
    if (!details) {
      return;
    }

    const rel = details.rel ?? 0;
    if (rel > 0) {
      track.classList.add('home-blog__track--fullwidth');
    } else {
      track.classList.remove('home-blog__track--fullwidth');
    }
  };

  const updateBlogNavState = (sliderInstance) => {
    const slider = sliderInstance ?? ctx.blogSliderInstance;
    const details = slider?.track?.details;
    if (!details) {
      return;
    }

    const maxIndex = Number.isFinite(details.maxIdx)
      ? details.maxIdx
      : Number.isFinite(details.max)
        ? details.max
        : Math.max(0, details.slides.length - 1);
    const rel = details.rel ?? 0;
    const minIndex = Number.isFinite(details.minIdx)
      ? details.minIdx
      : Number.isFinite(details.min)
        ? details.min
        : 0;
    const isAtStart = rel <= minIndex + Number.EPSILON;
    const isAtEnd = rel >= maxIndex - Number.EPSILON;

    setNavButtonState(prevBtn, isAtStart);
    setNavButtonState(nextBtn, isAtEnd);
    toggleBlogTrackWidth(details);
  };

  let readyTimeoutId = null;
  const clearReadyTimeout = () => {
    if (readyTimeoutId !== null) {
      window.clearTimeout(readyTimeoutId);
      readyTimeoutId = null;
    }
  };

  const markTrackReady = () => {
    clearReadyTimeout();
    track.classList.add('is-ready');
    track.removeAttribute('aria-hidden');
  };

  const revealTrackWhenReady = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        markTrackReady();
      });
    });
  };

  const handleSlideUpdate = (slider) => {
    updateBlogNavState(slider);
  };

  const handleSliderCreated = (slider) => {
    slider.update();

    adjustTrackPadding();
    updateBlogNavState(slider);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        slider.update();
        adjustTrackPadding();
        updateBlogNavState(slider);
        revealTrackWhenReady();
      });
    });
  };


  const sliderPlugin = (slider) => {
    slider.on('created', () => handleSliderCreated(slider));
    slider.on('updated', () => {
      adjustTrackPadding();
      updateBlogNavState(slider);
    });
    slider.on('slideChanged', () => handleSlideUpdate(slider));
    slider.on('animationEnded', () => handleSlideUpdate(slider));
    slider.on('animationStarted', () => { adjustTrackPadding(); });
  };

  ctx.blogSliderInstance = new KeenSlider(
    track,
    {
      loop: false,
      mode: 'snap',
      slides: {
        perView: 4,
        spacing: 0,
      },
      breakpoints: {
        '(max-width: 959px)': {
          slides: {
            perView: 2,
            spacing: 0,
          },
        },
        '(max-width: 639px)': {
          slides: {
            perView: 1,
            spacing: 0,
          },
        },
        '(max-width: 479px)': {
          slides: {
            perView: 1,
            spacing: 0,
          },
        },
      },
    },
    [sliderPlugin]
  );

  const refreshAfterMedia = () => {
    requestAnimationFrame(() => {
      ctx.blogSliderInstance?.update();
      adjustTrackPadding();
      updateBlogNavState();
    });
  };

  // Se houver imagens, atualiza quando carregarem (evita layout errado no início)
  const imgs = Array.from(track.querySelectorAll('img'));
  imgs.forEach((img) => {
    if (img.complete) return;
    img.addEventListener('load', refreshAfterMedia, { once: true });
    img.addEventListener('error', refreshAfterMedia, { once: true });
  });


  readyTimeoutId = window.setTimeout(markTrackReady, 600);

  const handleResize = () => {
    ctx.blogSliderInstance?.update();
  };

  window.addEventListener('resize', handleResize);

  prevBtn?.addEventListener('click', () => {
    ctx.blogSliderInstance?.prev();
  });

  nextBtn?.addEventListener('click', () => {
    ctx.blogSliderInstance?.next();
  });

  return ctx;
}
